import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Lock, Mail, Sparkles } from 'lucide-react';
import { loginSchema, type LoginInput } from '@/schemas';
import { api, ApiError } from '@/lib/api';
import { useAuthStore, type User } from '@/stores/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { LanguageSwitcher } from '@/components/i18n/LanguageSwitcher';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPwd, setShowPwd] = React.useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema) as never,
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const mutation = useMutation({
    mutationFn: async (input: LoginInput) => {
      return api.post<{
        accessToken: string;
        refreshToken: string;
        user: User;
      }>('/auth/login', input);
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken, data.refreshToken);
      toast.success('Bienvenido de vuelta');
      void navigate({ to: '/dashboard' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError ? err.message : t('auth.invalidCredentials');
      toast.error(message);
    },
  });

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left side: brand panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary/10 via-background to-background p-12 lg:flex">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-primary text-primary-foreground font-bold">
            C
          </div>
          <span className="text-lg font-semibold">{t('app.name')}</span>
        </div>
        <div className="space-y-6">
          <Sparkles className="h-10 w-10 text-primary" />
          <h1 className="text-4xl font-bold tracking-tight">
            El CMS más rápido del edge
          </h1>
          <p className="max-w-md text-lg text-muted-foreground">
            Construido sobre Cloudflare Workers, D1 y R2. TTFB global &lt;100ms,
            editor colaborativo, multi-idioma desde el primer día.
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>· TipTap + Yjs con colaboración en tiempo real</li>
            <li>· Multi-tenant con D1 por sitio</li>
            <li>· SEO checklist y OG automático</li>
            <li>· Marketplace de plugins</li>
          </ul>
        </div>
        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} CMS Express
        </div>
      </div>

      {/* Right side: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="absolute right-4 top-4 flex items-center gap-1">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
        <Card className="w-full max-w-md border-0 shadow-none sm:border sm:shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl">{t('auth.login')}</CardTitle>
            <CardDescription>
              Accede al panel central de CMS Express
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.email')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                            aria-hidden
                          />
                          <Input
                            type="email"
                            autoComplete="email"
                            className="pl-9"
                            placeholder="email@empresa.com"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.password')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                            aria-hidden
                          />
                          <Input
                            type={showPwd ? 'text' : 'password'}
                            autoComplete="current-password"
                            className="pl-9 pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPwd((s) => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            aria-label={
                              showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'
                            }
                          >
                            {showPwd ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-center justify-between">
                  <FormField
                    control={form.control}
                    name="rememberMe"
                    render={({ field }) => (
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => field.onChange(!!v)}
                        />
                        {t('auth.rememberMe')}
                      </label>
                    )}
                  />
                  <a
                    href="/forgot"
                    className="text-sm text-primary hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t('auth.login')}
                </Button>
              </form>
            </Form>

            <Separator className="my-6" />

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => toast.info(t('common.comingSoon'))}
            >
              {t('auth.magicLink')}
            </Button>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t('auth.noAccount')}{' '}
              <a href="/signup" className="text-primary hover:underline">
                {t('auth.signUp')}
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
